import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Avatar,
  Rating,
  Chip,
  Grid,
  Tabs,
  Tab,
} from "@mui/material";
import {
  Verified,
  DirectionsCar,
  Store,
  LocalOffer,
  Event,
  FormatQuote,
} from "@mui/icons-material";
import { motion, AnimatePresence } from "framer-motion";
import AnimatedSection from "../AnimatedSection";
import { gradients } from "../../../utils/theme";

/**
 * User Testimonials Section Component
 *
 * Premium design showcasing real shopper experiences with MallInHand.
 * Features category filtering, verified badges, and elegant card layouts.
 */

interface Testimonial {
  id: number;
  name: string;
  avatar: string;
  location: string;
  rating: 5;
  category: "parking" | "shopping" | "offers" | "events" | "all";
  text: string;
  feature: string;
  verified: boolean;
  date: string;
}

const UserTestimonialsSection: React.FC = () => {
  const [activeTab, setActiveTab] = useState<number>(0);

  const categories = [
    { value: "all", label: "All Experiences", icon: Verified },
    { value: "parking", label: "Parking", icon: DirectionsCar },
    { value: "shopping", label: "Shopping", icon: Store },
    { value: "offers", label: "Offers", icon: LocalOffer },
    { value: "events", label: "Events", icon: Event },
  ];

  const testimonials: Testimonial[] = [
    {
      id: 1,
      name: "Priya Sharma",
      avatar: "PS",
      location: "Mumbai",
      rating: 5,
      category: "parking",
      text: "No more parking nightmares! Bought my ticket on WhatsApp in 10 seconds, scanned QR and entered instantly. This is mall innovation at its finest!",
      feature: "Instant Parking",
      verified: true,
      date: "2 days ago",
    },
    {
      id: 2,
      name: "Rahul Mehta",
      avatar: "RM",
      location: "Bangalore",
      rating: 5,
      category: "shopping",
      text: "Found Zara in seconds using the store directory. No more wandering around confused. Saved me 30 minutes!",
      feature: "Store Finder",
      verified: true,
      date: "1 week ago",
    },
    {
      id: 3,
      name: "Anjali Desai",
      avatar: "AD",
      location: "Delhi",
      rating: 5,
      category: "offers",
      text: "Got a WhatsApp alert for 40% off at my favorite brand! Would've completely missed it. Already saved ₹3000 this month.",
      feature: "VIP Offers",
      verified: true,
      date: "3 days ago",
    },
    {
      id: 4,
      name: "Vikram Singh",
      avatar: "VS",
      location: "Hyderabad",
      rating: 5,
      category: "events",
      text: "Never miss food festivals or brand launches anymore. The event notifications are perfectly timed and super helpful!",
      feature: "Event Updates",
      verified: true,
      date: "5 days ago",
    },
    {
      id: 5,
      name: "Sneha Reddy",
      avatar: "SR",
      location: "Pune",
      rating: 5,
      category: "shopping",
      text: "Mall map on WhatsApp is genius! Found the restroom, ATM, and food court without asking anyone. So convenient!",
      feature: "Mall Navigator",
      verified: true,
      date: "1 week ago",
    },
    {
      id: 6,
      name: "Arjun Patel",
      avatar: "AP",
      location: "Chennai",
      rating: 5,
      category: "parking",
      text: "Weekend parking was always a 15-minute wait. Now? 5 seconds with QR entry. Life-changing for families with kids!",
      feature: "Fast Entry",
      verified: true,
      date: "4 days ago",
    },
  ];

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const filteredTestimonials =
    categories[activeTab].value === "all"
      ? testimonials
      : testimonials.filter((t) => t.category === categories[activeTab].value);

  return (
    <Box
      id="user-testimonials"
      sx={{
        py: { xs: 6, md: 10 },
        background: "linear-gradient(180deg, #ffffff 0%, #f9f9f9 100%)",
        position: "relative",
        overflow: "hidden",
      }}
    >
      <Container maxWidth="lg">
        <AnimatedSection>
          <Typography
            variant="h2"
            sx={{
              fontSize: { xs: "2rem", md: "2.5rem" },
              fontWeight: 700,
              mb: 2,
              textAlign: "center",
              background: gradients.navyLux,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
            }}
          >
            Shoppers Love MallInHand
          </Typography>
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              textAlign: "center",
              color: "text.secondary",
              fontWeight: 400,
            }}
          >
            Real experiences from thousands of happy shoppers
          </Typography>

          {/* Verified Badge */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 1,
              mb: 6,
            }}
          >
            <Verified sx={{ color: "#FFD700", fontSize: 20 }} />
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 500 }}
            >
              All reviews verified through WhatsApp
            </Typography>
          </Box>
        </AnimatedSection>

        {/* Category Tabs */}
        <AnimatedSection delay={0.2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mb: 6,
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Tabs
              value={activeTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  minHeight: 48,
                  color: "text.secondary",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    color: "#FFD700",
                  },
                  "&.Mui-selected": {
                    color: "#1a237e",
                  },
                },
                "& .MuiTabs-indicator": {
                  backgroundColor: "#FFD700",
                  height: 3,
                },
              }}
            >
              {categories.map((cat, index) => (
                <Tab
                  key={index}
                  icon={<cat.icon sx={{ fontSize: 20, mb: 0.5 }} />}
                  label={cat.label}
                  iconPosition="start"
                />
              ))}
            </Tabs>
          </Box>
        </AnimatedSection>

        {/* Testimonials Grid with AnimatePresence */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            <Grid container spacing={3}>
              {filteredTestimonials.map((testimonial, index) => (
                <Grid item xs={12} md={6} lg={4} key={testimonial.id}>
                  <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: 0.4,
                      delay: index * 0.1,
                      ease: [0.4, 0, 0.2, 1],
                    }}
                    whileHover={{ y: -8 }}
                  >
                    <Card
                      sx={{
                        height: "100%",
                        position: "relative",
                        border: "1px solid",
                        borderColor: "divider",
                        transition: "all 0.3s ease",
                        overflow: "visible",
                        background: "white",
                        "&:hover": {
                          boxShadow: "0 12px 40px rgba(255,215,0,0.15)",
                          borderColor: "#FFD700",
                          "& .quote-icon": {
                            transform: "scale(1.1) rotate(-5deg)",
                            color: "#FFD700",
                          },
                        },
                      }}
                    >
                      {/* Gradient Top Border */}
                      <Box
                        sx={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          height: 4,
                          background: gradients.gold,
                        }}
                      />

                      <CardContent sx={{ p: 3, pb: "24px !important" }}>
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            mb: 2,
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                            }}
                          >
                            <Avatar
                              sx={{
                                width: 48,
                                height: 48,
                                background: gradients.navyLux,
                                fontWeight: 700,
                                fontSize: "1.1rem",
                              }}
                            >
                              {testimonial.avatar}
                            </Avatar>
                            <Box>
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="subtitle1"
                                  fontWeight={600}
                                >
                                  {testimonial.name}
                                </Typography>
                                {testimonial.verified && (
                                  <Verified
                                    sx={{
                                      fontSize: 16,
                                      color: "#FFD700",
                                    }}
                                  />
                                )}
                              </Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ display: "block" }}
                              >
                                {testimonial.location} • {testimonial.date}
                              </Typography>
                            </Box>
                          </Box>

                          <FormatQuote
                            className="quote-icon"
                            sx={{
                              fontSize: 32,
                              color: "rgba(26, 35, 126, 0.1)",
                              transition: "all 0.3s ease",
                            }}
                          />
                        </Box>

                        {/* Rating */}
                        <Rating
                          value={testimonial.rating}
                          readOnly
                          size="small"
                          sx={{
                            mb: 2,
                            "& .MuiRating-iconFilled": {
                              color: "#FFD700",
                            },
                          }}
                        />

                        {/* Testimonial Text */}
                        <Typography
                          variant="body2"
                          sx={{
                            mb: 2,
                            color: "text.secondary",
                            lineHeight: 1.7,
                            fontStyle: "italic",
                          }}
                        >
                          "{testimonial.text}"
                        </Typography>

                        {/* Feature Tag */}
                        <Chip
                          label={testimonial.feature}
                          size="small"
                          sx={{
                            background:
                              "linear-gradient(135deg, rgba(26, 35, 126, 0.1) 0%, rgba(255, 215, 0, 0.1) 100%)",
                            color: "#1a237e",
                            fontWeight: 600,
                            fontSize: "0.75rem",
                            border: "1px solid rgba(255, 215, 0, 0.3)",
                          }}
                        />
                      </CardContent>
                    </Card>
                  </motion.div>
                </Grid>
              ))}
            </Grid>
          </motion.div>
        </AnimatePresence>

        {/* Bottom Stats */}
        <AnimatedSection delay={0.5}>
          <Box
            sx={{
              mt: 8,
              p: 4,
              borderRadius: 3,
              background: gradients.navyLux,
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              justifyContent: "space-around",
              alignItems: "center",
              gap: 4,
              boxShadow: "0 8px 32px rgba(26, 35, 126, 0.2)",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: gradients.gold,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  mb: 0.5,
                }}
              >
                4.9/5.0
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)" }}
              >
                Average Rating
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: gradients.gold,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  mb: 0.5,
                }}
              >
                50K+
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)" }}
              >
                Verified Reviews
              </Typography>
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  background: gradients.gold,
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text",
                  mb: 0.5,
                }}
              >
                98%
              </Typography>
              <Typography
                variant="body1"
                sx={{ color: "rgba(255,255,255,0.9)" }}
              >
                Would Recommend
              </Typography>
            </Box>
          </Box>
        </AnimatedSection>
      </Container>
    </Box>
  );
};

export default UserTestimonialsSection;
